import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuthStore()
  const { tenantInfo } = useTenantStore()
  const { t } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')

  // 테넌트 로고 URL (API 값 또는 기본 로고)
  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    clearError()

    // FormData에서 직접 값 읽기 (controlled input 동기화 문제 우회)
    const formData = new FormData(e.currentTarget)
    const emailValue = (formData.get('email') as string) || email
    const passwordValue = (formData.get('password') as string) || password

    console.log('[Login] Submitting with:', { email: emailValue, password: passwordValue ? '***' : '' })

    try {
      await login({ email: emailValue, password: passwordValue })
      // currentMode에 따라 적절한 페이지로 이동
      const { currentMode, requiresSetup } = useAuthStore.getState()
      if (requiresSetup) {
        navigate('/auth/setup')
      } else {
        navigate(currentMode === 'admin' ? '/admin' : '/my/courses')
      }
    } catch {
      // 에러는 store에서 처리
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantInfo?.name || t('login.companyLogo')}
              className="mx-auto h-16 w-auto"
            />
          ) : (
            <div
              className="mx-auto w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: tenantInfo?.primaryColor || '#6366f1' }}
            >
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {tenantInfo?.displayName || tenantInfo?.name || t('login.platformDefault')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('login.title')}
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {tCommon(`apiError.${error}`, { defaultValue: error })}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('login.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="name@company.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('login.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: tenantInfo?.primaryColor || undefined }}
          >
            {isLoading ? t('login.loggingIn') : t('login.submit')}
          </button>

          <div className="flex justify-center gap-4 text-sm">
            <Link to="/find-account" className="text-gray-500 hover:text-gray-700">
              {t('login.findAccount')}
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/reset-password" className="text-gray-500 hover:text-gray-700">
              {t('login.forgotPassword')}
            </Link>
          </div>

          {/* 회원가입 링크 제거 — B2B는 Admin이 계정 생성 */}
        </form>
      </div>
    </div>
  )
}
